import React, { FC, useState, useEffect, useCallback } from 'react';
import { Modal, Input, Button, Table, Select, Spin, Tag, Tooltip, message } from 'antd';
import { BinIcon } from 'components/Elements/CustomSVGIcon';
import { useDispatch } from 'react-redux';
import { AppDispatch } from 'core/types';
import {
    fetchCurrentSession,
    findStore,
    saveAndSend,
    removeEntry,
    sendReport,
    updateEntryStatus,
    SaveAndSendEntry,
    FindStoreResult,
    FindStoreMultipleResult,
    isMultipleSites,
} from 'core/ftc/ftcApi';
import { FtcSessionEntry } from 'core/sites/types';
import MultipleSitesPicker from '../MultipleSitesPicker';
import './ftcReportModal.scss';

type FtcStatus = 'ftc' | 'guarding' | 'no_access';

interface FtcReportModalProps {
    visible: boolean;
    onClose: () => void;
}

const STATUS_OPTIONS: Array<{ value: FtcStatus; label: string }> = [
    { value: 'ftc', label: 'Fail To Close (FTC)' },
    { value: 'guarding', label: 'Guarding' },
    { value: 'no_access', label: 'No Access OOH' },
];

const statusLabel = (status: string) => (
    STATUS_OPTIONS.find((option) => option.value === status)?.label ?? status
);

const FtcReportModal: FC<FtcReportModalProps> = ({ visible, onClose }) => {
    const dispatch = useDispatch<AppDispatch>();
    const [storeCode, setStoreCode] = useState('');
    const [savedEntries, setSavedEntries] = useState<FtcSessionEntry[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isFinding, setIsFinding] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [isConfirmingSend, setIsConfirmingSend] = useState(false);
    const [removingId, setRemovingId] = useState<number | null>(null);
    const [statusSavingId, setStatusSavingId] = useState<number | null>(null);
    // Stores sharing the searched post code, waiting for the user to pick one.
    const [siteChoices, setSiteChoices] = useState<FindStoreMultipleResult | null>(null);

    const loadSession = useCallback(async () => {
        setIsLoading(true);
        try {
            const session = await dispatch(fetchCurrentSession()).unwrap();
            setSavedEntries(session?.entries ?? []);
        } catch {
            // Error handled by thunk
        } finally {
            setIsLoading(false);
        }
    }, [dispatch]);

    useEffect(() => {
        if (visible) {
            loadSession();
        } else {
            setStoreCode('');
            setSavedEntries([]);
            setIsConfirmingSend(false);
            setSiteChoices(null);
        }
    }, [visible, loadSession]);

    const addStore = async (store: FindStoreResult) => {
        const payload: SaveAndSendEntry[] = [{
            siteId: store.siteId,
            storeCode: store.storeCode,
            responseId: store.responseId,
            status: store.suggestedStatus,
        }];
        await dispatch(saveAndSend(payload)).unwrap();
        // Cleared only once the store is on the report — a failed lookup
        // leaves the code in place to be corrected.
        setStoreCode('');
        setSiteChoices(null);
        message.success(`${store.siteName || store.storeCode} added`);
        await loadSession();
    };

    // Finding a store adds it to today's report there and then, with the status
    // the lookup suggested. The status stays editable in the table below, so
    // there is nothing to confirm and no separate save step.
    const handleFind = async () => {
        if (!storeCode.trim()) return;

        setIsFinding(true);
        setSiteChoices(null);
        try {
            const result = await dispatch(findStore(storeCode.trim())).unwrap();
            // A post code shared by several stores waits for the user to pick
            // one; nothing is added until they do.
            if (isMultipleSites(result)) {
                setSiteChoices(result);
                return;
            }
            await addStore(result);
        } catch {
            // Error handled by thunk
        } finally {
            setIsFinding(false);
        }
    };

    const handleSelectSite = async (store: FindStoreResult) => {
        setIsFinding(true);
        try {
            await addStore(store);
        } catch {
            // Error handled by thunk; the choices stay up to try again.
        } finally {
            setIsFinding(false);
        }
    };

    const handleStatusChange = async (entryId: number, status: FtcStatus) => {
        setStatusSavingId(entryId);
        try {
            const updated = await dispatch(updateEntryStatus({ entryId, status })).unwrap();
            setSavedEntries((entries) => entries.map(
                (entry) => (entry.id === entryId ? updated : entry),
            ));
        } catch {
            // Error handled by thunk
        } finally {
            setStatusSavingId(null);
        }
    };

    const handleRemoveEntry = async (entryId: number) => {
        setRemovingId(entryId);
        try {
            await dispatch(removeEntry(entryId)).unwrap();
            message.success('Store removed');
            await loadSession();
        } catch {
            // Error handled by thunk
        } finally {
            setRemovingId(null);
        }
    };

    const handleClose = () => {
        setStoreCode('');
        setIsConfirmingSend(false);
        setSiteChoices(null);
        onClose();
    };

    // The report cannot be recalled once sent, so the button opens a preview of
    // the stores going out and the send happens from there.
    const handleSendReport = async () => {
        setIsSending(true);
        try {
            const result = await dispatch(sendReport()).unwrap();
            message.success(result.message || 'FTC report sent successfully');
            handleClose();
        } catch {
            // Left on the preview so the send can be retried.
        } finally {
            setIsSending(false);
        }
    };

    // Sending only ever covers stores added since the last send
    const unsentEntries = savedEntries.filter((entry) => !entry.isSent);
    const sentCount = savedEntries.length - unsentEntries.length;

    // Columns shared by the saved list and the send preview.
    const storeColumns = [
        {
            title: 'Store Code',
            dataIndex: 'storeCode',
            key: 'storeCode',
            render: (val: string | null) => val || '-',
        },
        {
            title: 'Store Location',
            dataIndex: 'siteName',
            key: 'siteName',
            render: (val: string | null) => val || '-',
        },
        {
            title: 'Post Code',
            dataIndex: 'sitePostcode',
            key: 'sitePostcode',
            render: (val: string | null) => val || '-',
        },
    ];

    return (
        <>
            <Modal
                title="Fail to Close"
                open={visible}
                onCancel={() => !isSending && handleClose()}
                maskClosable={!isSending}
                keyboard={!isSending}
                closable={!isSending}
                footer={null}
                centered
                width={900}
                wrapClassName="ftc-report-modal">
                {isLoading ? (
                    <div className="modal-loading">
                        <Spin size="large" />
                    </div>
                ) : (
                    <>
                        <div className="add-store-row">
                            <Input
                                value={storeCode}
                                onChange={(e) => {
                                    setStoreCode(e.target.value);
                                    // The choices belong to the code that was searched.
                                    setSiteChoices(null);
                                }}
                                placeholder="Enter Store No or Post Code"
                                className="store-input"
                                onPressEnter={handleFind} />
                            <Button
                                type="primary"
                                onClick={handleFind}
                                loading={isFinding}
                                className="find-button">
                                Find
                            </Button>
                        </div>

                        {siteChoices && (
                            <MultipleSitesPicker
                                message={siteChoices.message}
                                sites={siteChoices.sites}
                                isSelecting={isFinding}
                                onSelect={handleSelectSite} />
                        )}

                        {savedEntries.length > 0 && (
                            <div className="saved-entries">
                                <h4 className="saved-entries-title">
                                    Saved today ({savedEntries.length})
                                </h4>
                                <Table
                                    dataSource={savedEntries}
                                    rowKey="id"
                                    pagination={false}
                                    size="small"
                                    className="entries-table"
                                    columns={[
                                        ...storeColumns,
                                        {
                                            title: 'Status',
                                            dataIndex: 'status',
                                            key: 'status',
                                            width: 220,
                                            // Sent stores are frozen for the same reason they
                                            // can't be removed: the report said otherwise.
                                            render: (val: string, record: FtcSessionEntry) => (
                                                <Select<FtcStatus>
                                                    value={val as FtcStatus}
                                                    onChange={(next) => handleStatusChange(record.id, next)}
                                                    options={STATUS_OPTIONS}
                                                    disabled={record.isSent}
                                                    loading={statusSavingId === record.id}
                                                    aria-label="Store status"
                                                    className="status-select" />
                                            ),
                                        },
                                        {
                                            title: 'Report',
                                            key: 'sent',
                                            width: 110,
                                            render: (_: unknown, record: FtcSessionEntry) => (
                                                record.isSent
                                                    ? <Tag color="green">Sent</Tag>
                                                    : <Tag color="orange">Not sent</Tag>
                                            ),
                                        },
                                        {
                                            title: ' ',
                                            key: 'actions',
                                            width: 60,
                                            align: 'right' as const,
                                            className: 'actions-cell',
                                            render: (_: unknown, record: FtcSessionEntry) => (
                                                // Sent stores can't be removed, the report has already gone out
                                                <Tooltip title={record.isSent ? 'Already sent, cannot be removed' : 'Remove store'}>
                                                    <span>
                                                        <Button
                                                            type="link"
                                                            className="bin-button"
                                                            disabled={record.isSent}
                                                            loading={removingId === record.id}
                                                            aria-label="Remove store"
                                                            onClick={() => handleRemoveEntry(record.id)}>
                                                            {removingId === record.id
                                                                ? null
                                                                : <BinIcon w={18} h={19} s={record.isSent ? '#B4BAC6' : '#D90429'} />}
                                                        </Button>
                                                    </span>
                                                </Tooltip>
                                            ),
                                        },
                                    ]} />
                            </div>
                        )}

                        <div className="modal-buttons">
                            <Tooltip title={savedEntries.length > 0 && unsentEntries.length === 0
                                ? 'Every store on today\'s report has already been sent. Add a store to send again.'
                                : ''}>
                                <span>
                                    <Button
                                        type="primary"
                                        className="send-report-button"
                                        loading={isSending}
                                        disabled={unsentEntries.length === 0 || isSending}
                                        onClick={() => setIsConfirmingSend(true)}>
                                        {sentCount > 0 ? 'Send New Stores' : 'Send Report'}
                                    </Button>
                                </span>
                            </Tooltip>
                            <Button
                                className="close-button"
                                disabled={isSending}
                                onClick={handleClose}>
                                Close
                            </Button>
                        </div>
                    </>
                )}
            </Modal>

            {/*
              * A sibling of the modal above rather than a child: nested inside
              * it, closing both at once left this one on screen over a closed
              * report modal.
              */}
            <Modal
                title={sentCount > 0 ? 'Send new stores' : 'Send FTC report'}
                open={visible && isConfirmingSend}
                // Dismissing mid-send would leave the request in flight with the
                // send button live again, so the report could go out twice.
                onCancel={() => !isSending && setIsConfirmingSend(false)}
                maskClosable={!isSending}
                keyboard={!isSending}
                closable={!isSending}
                footer={null}
                centered
                width={760}
                wrapClassName="ftc-report-modal">
                <p className="confirm-send-intro">
                    {unsentEntries.length === 1
                        ? 'This store will be sent:'
                        : `These ${unsentEntries.length} stores will be sent:`}
                </p>
                <Table
                    dataSource={unsentEntries}
                    rowKey="id"
                    pagination={false}
                    size="small"
                    className="entries-table"
                    columns={[
                        ...storeColumns,
                        {
                            title: 'Status',
                            dataIndex: 'status',
                            key: 'status',
                            render: (val: string) => statusLabel(val),
                        },
                    ]} />
                <div className="modal-buttons">
                    <Button
                        type="primary"
                        className="send-report-button"
                        loading={isSending}
                        onClick={handleSendReport}>
                        Confirm and Send
                    </Button>
                    <Button
                        className="close-button"
                        disabled={isSending}
                        onClick={() => setIsConfirmingSend(false)}>
                        Back
                    </Button>
                </div>
            </Modal>
        </>
    );
};

export default FtcReportModal;
